import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import focoLogo from '@/assets/foco-ai-logo.png';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'PASSWORD_RECOVERY' && session) {
          setIsValidSession(true);
          setIsChecking(false);
        }
      }
    );
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) setIsValidSession(true);
      setIsChecking(false);
    };
    checkSession();
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: 'Contraseña muy corta', description: 'La contraseña debe tener al menos 8 caracteres.', variant: 'destructive' });
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      toast({ title: 'Contraseña débil', description: 'Debe incluir al menos una mayúscula, una minúscula y un número.', variant: 'destructive' });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Las contraseñas no coinciden', description: 'Por favor verifica que ambas contraseñas sean iguales.', variant: 'destructive' });
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } else {
        setIsSuccess(true);
        toast({ title: '¡Contraseña actualizada!', description: 'Tu contraseña ha sido cambiada exitosamente.' });
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({ title: 'Error', description: 'Hubo un problema al actualizar la contraseña. Intenta de nuevo.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const LogoHeader = () => (
    <div className="flex items-center justify-center gap-3 mb-6">
      <img src={focoLogo} alt="FOCO AI System" className="h-14 sm:h-16 w-auto" />
    </div>
  );

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md space-y-6 text-center">
          <LogoHeader />
          <p className="text-muted-foreground">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  if (!isValidSession && !isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md space-y-6 text-center">
          <LogoHeader />
          <h2 className="text-2xl font-bold">Enlace inválido o expirado</h2>
          <p className="text-muted-foreground">El enlace de recuperación ha expirado o no es válido. Por favor solicita uno nuevo.</p>
          <Button onClick={() => navigate('/forgot-password')} className="w-full h-12">Solicitar nuevo enlace</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-8">
      <div className="w-full max-w-md space-y-6 sm:space-y-8">
        <div className="text-center">
          <LogoHeader />
          {isSuccess ? (
            <>
              <div className="mx-auto w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-8 w-8 text-success" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold">¡Contraseña actualizada!</h2>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">Serás redirigido al dashboard en unos segundos...</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold">Nueva contraseña</h2>
              <p className="text-muted-foreground mt-2 text-sm sm:text-base">Ingresa tu nueva contraseña para continuar.</p>
            </>
          )}
        </div>

        {!isSuccess && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium leading-none">Nueva contraseña</label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                  placeholder="••••••••" className="h-12 text-base pr-12" value={password}
                  onChange={(e) => setPassword(e.target.value)} required minLength={8} />
                <Button type="button" variant="ghost" size="icon"
                  className="absolute right-0 top-0 h-full w-12 px-3 touch-manipulation"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-muted-foreground" /> : <Eye className="h-5 w-5 text-muted-foreground" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres, con mayúscula, minúscula y número.</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium leading-none">Confirmar contraseña</label>
              <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password"
                placeholder="••••••••" className="h-12 text-base" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} required minLength={8} />
            </div>
            <Button type="submit" className="w-full h-12 text-base touch-manipulation" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Actualizar contraseña'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
